import type {
  ShopifyFilterGroup,
  ShopifyFilterOption,
  ShopifyFilterOptionsResponse,
  ShopifyQuantityPricing,
} from './storefront-api'

// Display rules copied from the Shopify theme's product discovery and product card.

const FILTER_GROUP_ORDER = [
  'category',
  'taxonomy',
  'manufacturer',
  'brand',
  'price',
  'free_shipping',
  'ship_days',
  'product_type',
  'spec.type',
  'spec.capacity',
  'spec.material',
  'spec.color',
  'spec.style',
  'spec.shape',
]
const FILTER_GROUP_RANK = new Map(FILTER_GROUP_ORDER.map((key, index) => [key, index]))
export const OPEN_BY_DEFAULT = new Set([
  'category',
  'manufacturer',
  'price',
  'ship_days',
  'product_type',
  'spec.type',
])
const DIMENSION_LABELS: Record<string, string> = {
  'spec.type': 'Product Type',
  'spec.shipping_height': 'Height',
  'spec.shipping_width': 'Width',
  'spec.shipping_depth': 'Depth',
  'spec.shipping_weight': 'Weight',
}

export function formatCount(value: number | null | undefined) {
  return (value ?? 0).toLocaleString('en-US')
}

export function displayFilterLabel(group: ShopifyFilterGroup) {
  return DIMENSION_LABELS[group.key] ?? group.label
}

export function usableOptions(group: ShopifyFilterGroup) {
  const seen = new Set<string>()
  return group.options.filter((option) => {
    const value = option.value.trim()
    if (!value || !option.label.trim() || seen.has(value)) return false
    seen.add(value)
    return true
  })
}

export function optionAvailability(option: ShopifyFilterOption) {
  return option.available_count ?? option.count ?? null
}

export function sanitizeRangeValue(value: string) {
  return value.replace(/[^0-9.,-]/g, '').replaceAll(',', '')
}

/** The storefront's filter groups, in its order, minus the fixed collection scope. */
export function storefrontFilterGroups(
  data: ShopifyFilterOptionsResponse | null,
  scopeKey?: string,
) {
  if (!data) return []
  return [data.price, ...data.facets]
    .filter((group): group is ShopifyFilterGroup => Boolean(group))
    .sort((left, right) => {
      const leftRank = FILTER_GROUP_RANK.get(left.key) ?? Number.MAX_SAFE_INTEGER
      const rightRank = FILTER_GROUP_RANK.get(right.key) ?? Number.MAX_SAFE_INTEGER
      if (leftRank !== rightRank) return leftRank - rightRank
      return left.key < right.key ? -1 : left.key > right.key ? 1 : 0
    })
    .filter((group) => group.key !== scopeKey)
    .filter((group) =>
      group.type === 'range'
        ? group.min != null || group.max != null || Boolean(group.selected)
        : usableOptions(group).length > 0,
    )
}

type PriceBreak = { minimumQuantity: number; unitPrice: string }

/** Collapse repeated trademark marks the same way the storefront card does. */
export function normalizeProductTitle(title: string) {
  return title.replace(/([\u2122\u00ae\u2120])(?:[\s\u00a0]*\1)+/gu, '$1')
}

export function productSaleUnit(unitPrice: string, unitsPerCase: string | null | undefined) {
  const caseQuantity = Number(unitsPerCase)
  if (!Number.isInteger(caseQuantity) || caseQuantity <= 1) {
    return { caseQuantity: null, eachUnitPrice: null }
  }
  const priceCents = Math.round(Number(unitPrice) * 100)
  if (!Number.isSafeInteger(priceCents) || priceCents < 0) {
    return { caseQuantity, eachUnitPrice: null }
  }
  return {
    caseQuantity,
    eachUnitPrice: (Math.round(priceCents / caseQuantity) / 100).toFixed(2),
  }
}

/** The storefront shows two tiers at most: a midpoint and the full discount. */
export function volumeDiscountLevels(pricing: ShopifyQuantityPricing): PriceBreak[] {
  const breaks = pricing.priceBreaks
  let shown = breaks
  const fullDiscount = breaks.at(-1)
  if (fullDiscount && breaks.length > 1) {
    const midpointQuantity = Math.ceil(fullDiscount.minimumQuantity / 2)
    let midpointPrice = pricing.quantityOnePrice
    for (const priceBreak of breaks) {
      if (priceBreak.minimumQuantity > midpointQuantity) break
      midpointPrice = priceBreak.unitPrice
    }
    shown = [
      ...(midpointQuantity > 1 && midpointQuantity < fullDiscount.minimumQuantity
        ? [{ minimumQuantity: midpointQuantity, unitPrice: midpointPrice }]
        : []),
      fullDiscount,
    ]
  }
  return [{ minimumQuantity: 1, unitPrice: pricing.quantityOnePrice }, ...shown]
}
